export const API_ENDPOINTS = {
  HEALTH: '/health',
  /** Public store update policy: GET /app-version/policy?platform=ANDROID|IOS */
  APP_VERSION: {
    POLICY: '/app-version/policy',
  },
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    ACTIVE_ROLE: '/auth/active-role',
    LOGOUT: '/auth/logout',
    CHANGE_PASSWORD: '/auth/change-password',
    FORGOT_PASSWORD: '/auth/forgot-password',
    FORGOT_PASSWORD_VERIFY_OTP: '/auth/forgot-password/verify-otp',
    FORGOT_PASSWORD_RESET: '/auth/forgot-password/reset',
  },
  ATTENDANCE: {
    VERIFY_FACE: '/attendance/verify-face',
    TODAY: '/attendance/today',
    HISTORY: '/attendance/history',
    /** HR org summary: GET /attendance/dashboard */
    DASHBOARD: '/attendance/dashboard',
    /** HR admin Leave/WFH/Permission/Compensatory Work: GET /attendance/hr-dashboard */
    HR_DASHBOARD: '/attendance/hr-dashboard',
    // Backend details route is date-based: GET /attendance/:attendanceDate
    DETAILS: (attendanceDate: string) => `/attendance/${attendanceDate}`,
    LATE_ARRIVALS: '/attendance/late-arrivals',
    WEEKLY_SUMMARY: '/attendance/weekly-summary',
    WEEKLY_REPORTS: '/attendance/weekly-reports',
    EMPLOYEE_OVERALL_WEEKLY_REPORT: '/attendance/employee-overall-weekly-report',
    /** Same path; pass format=XLSX|PDF for a binary download. */
    EMPLOYEE_OVERALL_WEEKLY_REPORT_DOWNLOAD:
      '/attendance/employee-overall-weekly-report',
    /** JSON grid; pass format=XLSX|PDF for a binary download. */
    EMPLOYEE_MONTHLY_ATTENDANCE_REPORT:
      '/attendance/employee-monthly-attendance-report',
    WFH_CHECK_IN: '/attendance/wfh-check-in',
    CHECK_IN: '/attendance/check-in',
    CHECK_OUT: '/attendance/check-out',
    BREAK_OUT: '/attendance/break-out',
    BREAK_IN: '/attendance/break-in',
    FACE_PUNCH: '/attendance/face-punch',
    /** Live location tracking for client-visit attendance. */
    TRACKING: {
      SETTINGS: '/attendance/tracking/settings',
      START: (attendanceId: number) => `/attendance/${attendanceId}/tracking/start`,
      POINTS: (attendanceId: number) => `/attendance/${attendanceId}/tracking/points`,
      DETAILS: (attendanceId: number) => `/attendance/${attendanceId}/tracking`,
    },
    CORRECTIONS: {
      HISTORY: '/attendance/corrections/history',
      APPLY: '/attendance/corrections',
      REQUEST_DETAILS: (correctionRequestId: number) =>
        `/attendance/corrections/${correctionRequestId}`,
      CANCEL: (correctionRequestId: number) =>
        `/attendance/corrections/${correctionRequestId}/cancel`,
      APPROVE: (correctionRequestId: number) =>
        `/attendance/corrections/${correctionRequestId}/approve`,
      REJECT: (correctionRequestId: number) =>
        `/attendance/corrections/${correctionRequestId}/reject`,
    },
  },
  /** Team Lead dashboard — backend scopes every call to the signed-in TL's team. */
  TEAM_DASHBOARD: {
    SUMMARY: '/team-dashboard/summary',
    ACTIVITY: '/team-dashboard/activity',
    BIRTHDAYS: '/team-dashboard/birthdays',
    ATTENDANCE_REGISTER: '/team-dashboard/attendance-register',
  },
  EMPLOYEES: {
    LIST: '/employees',
    CREATE: '/employees',
    DETAILS: (employeeId: number) => `/employees/${employeeId}`,
    UPDATE: (employeeId: number) => `/employees/${employeeId}`,
    DEACTIVATE: (employeeId: number) => `/employees/${employeeId}/deactivate`,
    ACTIVATE: (employeeId: number) => `/employees/${employeeId}/activate`,
    ASSIGN_SHIFT: (employeeId: number) => `/employees/${employeeId}/shift`,
    TIME_OFF_BALANCES: (employeeId: number) =>
      `/employees/${employeeId}/time-off-balances`,
    /** HR admin: GET (read) / PUT (enable or disable) WFH eligibility */
    WFH_ELIGIBILITY: (employeeId: number) =>
      `/employees/${employeeId}/wfh-eligibility`,
    /** HR admin: PUT change the employee's assigned WFH policy */
    WFH_POLICY: (employeeId: number) => `/employees/${employeeId}/wfh-policy`,
    /** HR admin: GET assigned/default/assignable, PUT the full set of leave policies */
    LEAVE_POLICIES: (employeeId: number) =>
      `/employees/${employeeId}/leave-policies`,
    AZURE_PERSON: (employeeId: number) =>
      `/employees/${employeeId}/azure-person`,
    ENSURE_AZURE_PERSON: (employeeId: number) =>
      `/employees/${employeeId}/azure-person/ensure`,
    // Override via FACE_ENROLLMENT_ADD_SAMPLE_PATH (must include {employeeId})
    ADD_FACE_SAMPLE: (employeeId: number) =>
      `/employees/${employeeId}/azure-person/faces`,
    FACE_REGISTRATION: (employeeId: number) =>
      `/employees/${employeeId}/face-registration`,
    DELETE_FACE_REGISTRATION: (employeeId: number) =>
      `/employees/${employeeId}/face-registration`,
    FACE_DELETION_STATUS: (employeeId: number) =>
      `/employees/${employeeId}/face-registration/deletion-status`,
    RETRY_FACE_DELETION: (employeeId: number) =>
      `/employees/${employeeId}/face-registration/retry-deletion`,
    /** HR admin upcoming birthdays: GET /employees/upcoming-birthdays */
    UPCOMING_BIRTHDAYS: '/employees/upcoming-birthdays',
    /** Roles the signed-in admin may assign: GET /employees/assignable-roles */
    ASSIGNABLE_ROLES: '/employees/assignable-roles',
  },
  FACE: {
    IDENTIFY: '/face/identify',
    VERIFICATIONS: '/face-verifications',
    CREATE_LIVENESS_SESSION: '/face/liveness/sessions',
    GET_LIVENESS_SESSION: (livenessSessionId: number | string) =>
      `/face/liveness/sessions/${livenessSessionId}`,
    DELETE_LIVENESS_SESSION: (livenessSessionId: number | string) =>
      `/face/liveness/sessions/${livenessSessionId}`,
    PERSON_GROUP: '/face/person-group',
    ENSURE_PERSON_GROUP: '/face/person-group/ensure',
    TRAIN_PERSON_GROUP: '/face/person-group/train',
    TRAIN_PERSON_GROUP_AND_WAIT: '/face/person-group/train-and-wait',
    TRAINING_STATUS: '/face/person-group/training-status',
    OPERATIONAL_STATUS: '/face/admin/operational-status',
    ENSURE_AWS_PROVIDER: '/face/admin/providers/aws/ensure',
    SYNC_PROBLEMS: '/face/admin/sync-problems',
    PRIVACY_CLEANUP: '/face/admin/privacy-cleanup',
  },
  WORKFLOW: {
    APPROVALS: '/workflows/approvals',
    PENDING_APPROVALS: '/workflows/approvals/pending',
    DETAILS: (workflowInstanceId: number) => `/workflows/${workflowInstanceId}`,
    HISTORY: (workflowInstanceId: number) =>
      `/workflows/${workflowInstanceId}/history`,
  },
  LEAVE: {
    DASHBOARD: '/leave/dashboard',
    BALANCES: '/leave/balances',
    HISTORY: '/leave/history',
    SESSION_TYPES: '/leave/session-types',
    APPLY: '/leave',
    REQUEST_DETAILS: (leaveApplicationId: number) =>
      `/leave/${leaveApplicationId}`,
    CANCEL: (leaveApplicationId: number) =>
      `/leave/${leaveApplicationId}/cancel`,
    APPROVE: (leaveApplicationId: number) =>
      `/leave/${leaveApplicationId}/approve`,
    REJECT: (leaveApplicationId: number) =>
      `/leave/${leaveApplicationId}/reject`,
  },
  LEAVE_CONFIGURATIONS: {
    LIST: '/leave-configurations',
    CREATE: '/leave-configurations',
    UPDATE: (leavePolicyId: number) => `/leave-configurations/${leavePolicyId}`,
    GENERATE_BALANCES: (leavePolicyId: number) =>
      `/leave-configurations/${leavePolicyId}/balances`,
  },
  LEAVE_BALANCES: {
    LIST: '/leave-balances',
    EMPLOYEE: (employeeId: number) => `/leave-balances/employees/${employeeId}`,
  },
  COMPENSATORY_WORK: {
    LIST: '/compensatory-work',
    CREATE: '/compensatory-work',
    DECIDE: (requestId: number) => `/compensatory-work/${requestId}/decision`,
    CANCEL: (requestId: number) => `/compensatory-work/${requestId}/cancel`,
  },
  HR_POLICY_CONFIGURATIONS: {
    WFH: '/hr-policy-configurations/wfh',
    UPDATE_WFH: (wfhPolicyId: number) =>
      `/hr-policy-configurations/wfh/${wfhPolicyId}`,
    PERMISSION: '/hr-policy-configurations/permission',
    PERMISSION_POLICY: '/hr-policy-configurations/permission-policy',
    UPDATE_PERMISSION: (permissionPolicyId: number) =>
      `/hr-policy-configurations/permission/${permissionPolicyId}`,
    LATE_ARRIVAL: '/hr-policy-configurations/late-arrival',
  },
  WFH: {
    DASHBOARD: '/wfh/dashboard',
    HISTORY: '/wfh/history',
    APPLY: '/wfh',
    SESSION_TYPES: '/wfh/session-types',
    TEAM_MEMBERS: '/wfh/team-members',
    REQUEST_DETAILS: (wfhRequestId: number) => `/wfh/${wfhRequestId}`,
    ATTENDANCE_LOG: (wfhRequestId: number) =>
      `/wfh/${wfhRequestId}/attendance-log`,
    CANCEL: (wfhRequestId: number) => `/wfh/${wfhRequestId}/cancel`,
    APPROVE: (wfhRequestId: number) => `/wfh/${wfhRequestId}/approve`,
    REJECT: (wfhRequestId: number) => `/wfh/${wfhRequestId}/reject`,
  },
  TIME_PERMISSION: {
    DASHBOARD: '/permissions/dashboard',
    HISTORY: '/permissions/history',
    TYPES: '/permissions/types',
    APPLY: '/permissions',
    REQUEST_DETAILS: (permissionRequestId: number) =>
      `/permissions/${permissionRequestId}`,
    CANCEL: (permissionRequestId: number) =>
      `/permissions/${permissionRequestId}/cancel`,
    APPROVE: (permissionRequestId: number) =>
      `/permissions/${permissionRequestId}/approve`,
    REJECT: (permissionRequestId: number) =>
      `/permissions/${permissionRequestId}/reject`,
  },
  NOTIFICATIONS: {
    LIST: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    DETAILS: (notificationId: number) => `/notifications/${notificationId}`,
    MARK_READ: (notificationId: number) =>
      `/notifications/${notificationId}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    PREFERENCES: '/notifications/preferences',
    PREFERENCE: (notificationType: string) =>
      `/notifications/preferences/${encodeURIComponent(notificationType)}`,
  },
  /**
   * Confirmed report routes (arkhrmsapi report.routes.js).
   * CSV/XLSX use POST RUN with body format + download=true.
   * Execution history routes are not mounted.
   */
  REPORTS: {
    DEFINITIONS: '/reports',
    RUN: (reportCode: string) =>
      `/reports/${encodeURIComponent(reportCode)}/run`,
  },
  /**
   * Confirmed organization master-data routes (arkhrmsapi organization.routes.js).
   * Mounted under /organization (singular). No delete routes. No get-by-id for
   * locations/departments/designations/teams — details use search ownership.
   */
  ORGANIZATION: {
    CURRENT: '/organization',
    UPDATE_CURRENT: '/organization',
  },
  ORGANIZATIONS: {
    LIST: '/organizations',
    CREATE: '/organizations',
    DETAILS: (organizationId: number) => `/organizations/${organizationId}`,
    CREATE_HR_ADMIN: (organizationId: number) =>
      `/organizations/${organizationId}/hr-admins`,
  },
  LOCATIONS: {
    LIST: '/organization/locations',
    CREATE: '/organization/locations',
    UPDATE: (locationId: number) => `/organization/locations/${locationId}`,
  },
  DEPARTMENTS: {
    LIST: '/organization/departments',
    CREATE: '/organization/departments',
    UPDATE: (departmentId: number) =>
      `/organization/departments/${departmentId}`,
  },
  DESIGNATIONS: {
    LIST: '/organization/designations',
    CREATE: '/organization/designations',
    UPDATE: (designationId: number) =>
      `/organization/designations/${designationId}`,
  },
  TEAMS: {
    LIST: '/organization/teams',
    CREATE: '/organization/teams',
    UPDATE: (teamId: number) => `/organization/teams/${teamId}`,
    MEMBERS: (teamId: number) => `/organization/teams/${teamId}/members`,
    REMOVE_MEMBER: (teamId: number, employeeId: number) =>
      `/organization/teams/${teamId}/members/${employeeId}`,
  },
  /**
   * Confirmed shift routes (arkhrmsapi shift.routes.js).
   * No GET-by-id or DELETE. Details use search ownership.
   */
  SHIFTS: {
    LIST: '/shifts',
    CREATE: '/shifts',
    UPDATE: (shiftId: number) => `/shifts/${shiftId}`,
  },
  /**
   * Confirmed work-policy routes (arkhrmsapi work-policy.routes.js).
   * No GET-by-id or DELETE. Details use search ownership.
   */
  WORK_POLICIES: {
    LIST: '/work-policies',
    CREATE: '/work-policies',
    UPDATE: (workPolicyId: number) => `/work-policies/${workPolicyId}`,
  },
  /**
   * Confirmed kiosk routes (arkhrmsapi kiosk.routes.js).
   * Device activate/refresh/status are unauthenticated; admin routes need SETTINGS_*.
   */
  KIOSKS: {
    LIST: '/kiosks',
    CREATE: '/kiosks',
    DETAILS: (kioskId: number) => `/kiosks/${kioskId}`,
    ACTIVATION_TOKEN: (kioskId: number) =>
      `/kiosks/${kioskId}/activation-token`,
    DEACTIVATE: (kioskId: number) => `/kiosks/${kioskId}/deactivate`,
    KIOSK_ACTIVATION_STATUS: (kioskId: number) =>
      `/kiosks/${kioskId}/activation-status`,
    ACTIVATE: '/kiosks/activate',
    REFRESH: '/kiosks/auth/refresh',
    ACTIVATION_STATUS: '/kiosks/activation-status',
  },
  HOLIDAY_CALENDARS: {
    LIST: '/holiday-calendars',
    CREATE: '/holiday-calendars',
    DETAILS: (holidayCalendarId: number) =>
      `/holiday-calendars/${holidayCalendarId}`,
    UPDATE: (holidayCalendarId: number) =>
      `/holiday-calendars/${holidayCalendarId}`,
    DELETE: (holidayCalendarId: number) =>
      `/holiday-calendars/${holidayCalendarId}`,
    EMPLOYEE_ASSIGNMENTS: '/holiday-calendars/employee-assignments',
  },
  CALENDAR: {
    MONTHLY: '/calendar',
    UPCOMING_HOLIDAYS: '/calendar/upcoming-holidays',
    CREATE_HOLIDAY: '/calendar/holidays',
    UPDATE_HOLIDAY: (holidayId: number) => `/calendar/holidays/${holidayId}`,
    DELETE_HOLIDAY: (holidayId: number) => `/calendar/holidays/${holidayId}`,
    WORKING_DAYS: '/calendar/working-days',
    MONTH_NOTES: '/calendar/notes',
  },
  VISITORS: {
    LIST: '/visitors',
    REGISTER: '/visitors',
    VISITS: '/visitors/visits',
    CHECK_IN: '/visitors/check-in',
    CHECK_OUT: '/visitors/check-out',
    QUICK_REGISTER: '/visitors/quick-register',
    RECOGNIZE: '/visitors/recognize',
    HR_CONTACT: '/visitors/hr-contact',
    DELETE: (visitorId: number) => `/visitors/${visitorId}`,
  },
  PROJECTS: {
    LIST: '/projects',
    CREATE: '/projects',
    ASSIGNABLE_EMPLOYEES: '/projects/assignable-employees',
    REPORT_EMPLOYEES: (projectId: number) =>
      `/projects/${projectId}/reports/employees`,
    REPORT_TASKS: (projectId: number) => `/projects/${projectId}/reports/tasks`,
    DETAILS: (projectId: number) => `/projects/${projectId}`,
    ADD_MEMBERS: (projectId: number) => `/projects/${projectId}/members/bulk`,
    BOARD: (projectId: number) => `/projects/${projectId}/board`,
    COMPLETED_TASKS: (projectId: number) => `/projects/${projectId}/completed-tasks`,
    CREATE_TASK: (projectId: number) => `/projects/${projectId}/tasks`,
    TASK_DETAILS: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}`,
    UPDATE_TASK: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}`,
    MOVE_TASK: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}/move`,
    CREATE_SUBTASK: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}/subtasks`,
    REORDER_SUBTASKS: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}/subtasks/reorder`,
    UPDATE_SUBTASK: (projectId: number, subtaskId: number) =>
      `/projects/${projectId}/subtasks/${subtaskId}`,
    DELETE_TASK: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}`,
    DELETE_SUBTASK: (projectId: number, subtaskId: number) =>
      `/projects/${projectId}/subtasks/${subtaskId}`,
    TIME_LOGS: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}/time-logs`,
    UPDATE_TIME_LOG: (projectId: number, taskId: number, timeLogId: number) =>
      `/projects/${projectId}/tasks/${taskId}/time-logs/${timeLogId}`,
    DELETE_TIME_LOG: (projectId: number, taskId: number, timeLogId: number) =>
      `/projects/${projectId}/tasks/${taskId}/time-logs/${timeLogId}`,
    FOCUS_SESSIONS: (projectId: number, taskId: number) =>
      `/projects/${projectId}/tasks/${taskId}/focus-sessions`,
  },
} as const;

export const apiEndpoints = {
  health: API_ENDPOINTS.HEALTH,
} as const;
